import {
  appointmentOptionsSchema,
  appointmentResponseSchema,
  appointmentsResponseSchema,
  googleCalendarStatusSchema,
  type AppointmentView,
  type BookAppointmentBody,
} from "@gioia365/contracts";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../api/http";
import { leadKeys } from "../leads/lead-queries";
import { tabKey, type TabContext } from "../session/tab-context";
import { opportunityKeys } from "../opportunities/opportunity-keys";
import { taskKeys } from "../tasks/task-keys";

// Le letture e le scritture di disco e demo (piano M6, R7, Task 6): gli appuntamenti del Lead con la sua opportunità aperta, le
// scelte della finestra (fuso, email del Lead, Calendar collegato, colleghi), la prenotazione e i comandi «Sposta», «Annulla» ed
// esito, sempre con la versione letta. Ogni scrittura rilegge la scheda, la Timeline, i Task, il banner e le Opportunità (R8).

const leadPath = (leadId: string) => `/api/leads/${encodeURIComponent(leadId)}/appointments`;

export const appointmentKeys = {
  lead: (tab: ReturnType<typeof tabKey>, leadId: string, view: string | null) => ["scheda", tab, "appointments", view, leadId] as const,
  options: (tab: ReturnType<typeof tabKey>, leadId: string, view: string | null) => ["scheda", tab, "appointments", view, leadId, "options"] as const,
  googleStatus: ["google-calendar", "status"] as const,
};

export function appointmentsQuery(tab: TabContext, leadId: string, view: string | null) {
  return queryOptions({
    queryKey: appointmentKeys.lead(tabKey(tab), leadId, view),
    queryFn: ({ signal }) => apiRequest("GET", leadPath(leadId), { schema: appointmentsResponseSchema, tab, signal }),
    staleTime: 30_000,
    // Ogni minuto: gli esiti si offrono dall'ora dell'appuntamento e il link Meet arriva quando Google lo restituisce.
    refetchInterval: 60_000,
  });
}

export function appointmentOptionsQuery(tab: TabContext, leadId: string, view: string | null) {
  return queryOptions({
    queryKey: appointmentKeys.options(tabKey(tab), leadId, view),
    queryFn: ({ signal }) => apiRequest("GET", `${leadPath(leadId)}/options`, { schema: appointmentOptionsSchema, tab, signal }),
    staleTime: 0,
  });
}

/** Lo stato del Google Calendar della persona: vale per l'identità, non per la scheda. */
export function googleCalendarStatusQuery() {
  return queryOptions({
    queryKey: appointmentKeys.googleStatus,
    queryFn: ({ signal }) => apiRequest("GET", "/api/google/status", { schema: googleCalendarStatusSchema, signal }),
    staleTime: 60_000,
  });
}

function useAppointmentWrite<Input>(tab: TabContext, leadId: string, view: string | null, send: (input: Input) => Promise<{ appointment: AppointmentView }>) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return useMutation({
    mutationFn: send,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.lead(tabPart, leadId, view) });
      void queryClient.invalidateQueries({ queryKey: leadKeys.card(tabPart, leadId, view), exact: true });
      void queryClient.invalidateQueries({ queryKey: leadKeys.timelines(tabPart, leadId, view) });
      void queryClient.invalidateQueries({ queryKey: taskKeys.all(tabPart) });
      // La prenotazione apre o fa avanzare l'Opportunità, l'esito la fa avanzare (R8, Task 25: il riquadro e la pagina la seguono).
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.all(tabPart) });
    },
  });
}

export function useBookAppointment(tab: TabContext, leadId: string, view: string | null) {
  return useAppointmentWrite(tab, leadId, view, (body: BookAppointmentBody) => apiRequest("POST", leadPath(leadId), { schema: appointmentResponseSchema, body, tab }));
}

export type AppointmentCommand =
  | { action: "reschedule"; startsAt: string; version: number }
  | { action: "cancel"; reason: string; version: number }
  | { action: "result"; result: "held" | "no_show"; heldMinutes?: number; version: number };

export function useChangeAppointment(tab: TabContext, leadId: string, view: string | null) {
  return useAppointmentWrite(tab, leadId, view, ({ taskId, command }: { taskId: string; command: AppointmentCommand }) => {
    const { action, ...body } = command;
    return apiRequest("POST", `/api/appointments/${encodeURIComponent(taskId)}/${action}`, { schema: appointmentResponseSchema, body, tab });
  });
}
